import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, Clock3, FileText, MapPin, Plus, Search, Stethoscope, UserPlus, Video } from 'lucide-react';
import { cn } from '../utils/cn';
import InitialAvatar from './InitialAvatar';
import { API_BASE } from '../config/env';
import { useI18n } from '../i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';

type AppointmentStatus = 'SCHEDULED' | 'PORTAL_BOOKED' | 'CHECKED_IN' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

type AppointmentTypeOption = {
  id: string;
  name: string;
  color: string;
  durationMinutes: number;
  isTelemed?: boolean;
  serviceDocumentation?: Record<string, unknown>;
  serviceVariants?: Array<{
    id: string;
    code: string;
    name: string;
    category?: string | null;
    price: number;
    active: boolean;
  }>;
};

type ResourceOption = {
  id: string;
  name: string;
  type: string;
  locationId?: string | null;
};

type MedicalFormOption = {
  id: string;
  name: string;
  code?: string | null;
  category?: string | null;
};

type AppointmentMedicalForm = {
  instanceId: string;
  templateId: string;
  templateName: string;
  status: string;
  sentAt?: string | null;
  completedAt?: string | null;
};

type PatientOption = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  mrn?: string | null;
  phone?: string | null;
  dob?: string | null;
};

type CreatePatientForm = {
  firstName: string;
  lastName: string;
  dob: string;
  mrn: string;
  phone: string;
  email: string;
  sex: string;
};

type SmartSuggestion = {
  providerUserId: string;
  providerName: string;
  providerTitle?: string | null;
  appointmentTypeName: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  score: number;
  reasons: string[];
};

type AppointmentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (appointment: any) => Promise<void>;
  onAppointmentActionComplete?: (appointmentId: string) => Promise<void> | void;
  initialData?: any;
  providerOptions: Array<{ id: string; name: string; avatarUrl?: string | null }>;
  patientOptions: PatientOption[];
  appointmentTypes: AppointmentTypeOption[];
  locationOptions: Array<{ id: string; name: string; isPrimary?: boolean }>;
  roomOptions: ResourceOption[];
  assetOptions: ResourceOption[];
  medicalFormOptions: MedicalFormOption[];
  serviceVariantsEnabled: boolean;
  defaultSlotMinutes: number;
  getToken: () => Promise<string | null>;
};

type PatientNote = {
  id: string;
  title: string;
  contentText: string;
  contentHtml: string;
};

const APPOINTMENT_NOTE_MARKER_PREFIX = 'APPT-ID:';

const emptyCreatePatientForm = (): CreatePatientForm => ({
  firstName: '',
  lastName: '',
  dob: '',
  mrn: '',
  phone: '',
  email: '',
  sex: '',
});

const statusLabels: Record<AppointmentStatus, string> = {
  SCHEDULED: 'Scheduled',
  PORTAL_BOOKED: 'Portal booked',
  CHECKED_IN: 'Checked-in',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No-show',
};

function equipmentIdsForType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  return Array.isArray(meta.equipmentResourceIds) ? meta.equipmentResourceIds.map(String) : [];
}

function virtualMetaForType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  const virtualDeliveryMode = meta.virtualDeliveryMode === 'hybrid' || meta.virtualDeliveryMode === 'virtual'
    ? meta.virtualDeliveryMode
    : (type?.isTelemed ? 'virtual' : 'in_person');
  return {
    virtualDeliveryMode,
    videoProviderOverride: String(meta.videoProviderOverride || ''),
    groupVideoEnabled: Boolean(meta.groupVideoEnabled),
    maxVideoParticipants: Number(meta.maxVideoParticipants || 2),
    autoCreateVideoSession: Boolean(meta.autoCreateVideoSession ?? true),
    videoConsultationInstructions: String(meta.videoConsultationInstructions || ''),
    videoTroubleshootingFaq: String(meta.videoTroubleshootingFaq || ''),
  };
}

function classMetaForType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  return {
    isClass: Boolean(meta.isClass),
    classCapacity: Number(meta.classCapacity || 8),
    classOnlineBookingEnabled: Boolean(meta.classOnlineBookingEnabled ?? true),
    classPortalVisible: Boolean(meta.classPortalVisible ?? true),
    classNotificationsEnabled: Boolean(meta.classNotificationsEnabled ?? true),
    classWaitlistEnabled: Boolean(meta.classWaitlistEnabled),
    classParticipantInstructions: String(meta.classParticipantInstructions || ''),
  };
}

function timingMetaForType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  const baseDuration = Number(type?.durationMinutes || 0);
  const extraBeforeMinutes = Math.max(0, Number(meta.extraBeforeMinutes || 0));
  const extraAfterMinutes = Math.max(0, Number(meta.extraAfterMinutes || 0));
  return {
    bufferBeforeMinutes: Math.max(0, Number(meta.bufferBeforeMinutes || 0)),
    bufferAfterMinutes: Math.max(0, Number(meta.bufferAfterMinutes || 0)),
    extraBeforeMinutes,
    extraAfterMinutes,
    effectiveDurationMinutes: Math.max(5, baseDuration + extraBeforeMinutes + extraAfterMinutes),
  };
}

export default function AppointmentModal({
  isOpen,
  onClose,
  onSubmit,
  onAppointmentActionComplete,
  initialData,
  providerOptions,
  patientOptions,
  appointmentTypes,
  locationOptions,
  roomOptions,
  assetOptions,
  medicalFormOptions,
  serviceVariantsEnabled,
  defaultSlotMinutes,
  getToken,
}: AppointmentModalProps) {
  const navigate = useNavigate();
  const { tr, language } = useI18n();
  const locale = language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';
  const [pending, setPending] = useState<{
    id?: string;
    encounterId?: string | null;
    patientId: string;
    providerId: string;
    status: AppointmentStatus;
    appointmentTypeId: string;
    billingServiceId?: string;
    locationId: string;
    start: string;
    durationMinutes: number;
    title: string;
    isTelemed?: boolean;
    generateTelemedLink?: boolean;
    joinUrl?: string | null;
    roomResourceId?: string;
    assetResourceIds: string[];
    additionalStaffIds: string[];
    medicalFormTemplateIds: string[];
    emailReminderEnabled: boolean;
    smsReminderEnabled: boolean;
    reminderMinutesBefore: number;
    multiServiceIds: string[];
    packageBookingEnabled: boolean;
    packageId: string;
    packageQuantity: number;
    classBookingEnabled: boolean;
    classParticipantIds: string[];
    classParticipantStatuses: Record<string, AppointmentStatus>;
    classCapacity: number;
    classNotificationsEnabled: boolean;
    repeatEnabled: boolean;
    repeatFrequency: 'daily' | 'weekly' | 'monthly';
    repeatCount: number;
    bookingDepositEnabled: boolean;
    bookingDepositAmount: number;
    paymentHoldEnabled: boolean;
    paymentHoldReference: string;
  }>({
    id: undefined,
    patientId: '',
      encounterId: null,
    providerId: '',
    status: 'SCHEDULED',
    appointmentTypeId: '',
    billingServiceId: '',
    locationId: '',
    start: '',
    durationMinutes: defaultSlotMinutes,
    title: '',
    isTelemed: false,
    generateTelemedLink: false,
    joinUrl: null,
    roomResourceId: '',
    assetResourceIds: [],
    additionalStaffIds: [],
    medicalFormTemplateIds: [],
    emailReminderEnabled: false,
    smsReminderEnabled: false,
    reminderMinutesBefore: 1440,
    multiServiceIds: [],
    packageBookingEnabled: false,
    packageId: '',
    packageQuantity: 1,
    classBookingEnabled: false,
    classParticipantIds: [],
    classParticipantStatuses: {},
    classCapacity: 8,
    classNotificationsEnabled: true,
    repeatEnabled: false,
    repeatFrequency: 'weekly',
    repeatCount: 1,
    bookingDepositEnabled: false,
    bookingDepositAmount: 0,
    paymentHoldEnabled: false,
    paymentHoldReference: '',
  });

  const [patientQuery, setPatientQuery] = useState('');
  const [providerQuery, setProviderQuery] = useState('');
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [showProviderResults, setShowProviderResults] = useState(false);
  const [createdPatientOptions, setCreatedPatientOptions] = useState<PatientOption[]>([]);
  const [showCreatePatientDialog, setShowCreatePatientDialog] = useState(false);
  const [createPatientForm, setCreatePatientForm] = useState<CreatePatientForm>(() => emptyCreatePatientForm());
  const [createPatientError, setCreatePatientError] = useState<string | null>(null);
  const [createPatientSaving, setCreatePatientSaving] = useState(false);
  const [medicalFormQuery, setMedicalFormQuery] = useState('');
  const [showMedicalFormResults, setShowMedicalFormResults] = useState(false);
  const [patientDetail, setPatientDetail] = useState<any | null>(null);
  const [appointmentNoteId, setAppointmentNoteId] = useState<string | null>(null);
  const [noteActionBusy, setNoteActionBusy] = useState(false);
  const [smartSuggestions, setSmartSuggestions] = useState<SmartSuggestion[]>([]);
  const [smartSuggestBusy, setSmartSuggestBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visitActionBusy, setVisitActionBusy] = useState(false);
  const [automationBusyAction, setAutomationBusyAction] = useState<string | null>(null);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const quickActionsRef = useRef<HTMLDivElement | null>(null);
  const fieldClass =
    'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10 bg-white';
  const defaultLocationId = locationOptions.find((location) => location.isPrimary)?.id || locationOptions[0]?.id || '';
  const allPatientOptions = useMemo(() => {
    const byId = new Map<string, PatientOption>();
    [...createdPatientOptions, ...patientOptions].forEach((patient) => byId.set(patient.id, patient));
    return Array.from(byId.values());
  }, [createdPatientOptions, patientOptions]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      const firstTelemedType = appointmentTypes.find((type) => type.isTelemed);
      const incomingType = appointmentTypes.find((type) => type.id === initialData.appointmentTypeId);
      const shouldForceTelemedType = Boolean(initialData.isTelemed) && (!incomingType || !incomingType.isTelemed);
      const effectiveType = shouldForceTelemedType ? firstTelemedType : incomingType;
      const effectiveClassMeta = classMetaForType(effectiveType);
      const effectiveTimingMeta = timingMetaForType(effectiveType);
      const nextPending = {
        id: initialData.id,
        encounterId: initialData.encounterId ?? null,
        patientId: initialData.patientId ?? '',
        providerId: initialData.providerId ?? (providerOptions[0]?.id || ''),
        status: initialData.status ?? 'SCHEDULED',
        appointmentTypeId:
          (shouldForceTelemedType ? firstTelemedType?.id : initialData.appointmentTypeId) || appointmentTypes[0]?.id || '',
        billingServiceId: serviceVariantsEnabled ? initialData.billingServiceId ?? initialData.billingService?.id ?? '' : '',
        locationId: initialData.locationId || defaultLocationId,
        start: initialData.start || new Date().toISOString().slice(0, 16),
        durationMinutes: initialData.durationMinutes || effectiveTimingMeta.effectiveDurationMinutes || effectiveType?.durationMinutes || defaultSlotMinutes,
        title: initialData.title || '',
        isTelemed: initialData.isTelemed ?? effectiveType?.isTelemed ?? false,
        generateTelemedLink: initialData.generateTelemedLink ?? Boolean(initialData.isTelemed ?? effectiveType?.isTelemed),
        joinUrl: initialData.joinUrl ?? null,
        roomResourceId: initialData.roomResourceId ?? '',
        assetResourceIds: Array.isArray(initialData.assetResourceIds) ? initialData.assetResourceIds : [],
        additionalStaffIds: Array.isArray(initialData.additionalStaffIds) ? initialData.additionalStaffIds : [],
        medicalFormTemplateIds: Array.isArray(initialData.medicalForms)
          ? initialData.medicalForms
              .map((item: AppointmentMedicalForm) => item.templateId)
              .filter((item: string | undefined): item is string => Boolean(item))
          : [],
        emailReminderEnabled: Boolean(initialData.emailReminderEnabled),
        smsReminderEnabled: Boolean(initialData.smsReminderEnabled),
        reminderMinutesBefore: initialData.reminderMinutesBefore ?? 1440,
        multiServiceIds: Array.isArray(initialData.multiServiceIds) ? initialData.multiServiceIds : [],
        packageBookingEnabled: Boolean(initialData.packageBookingEnabled),
        packageId: initialData.packageId ?? '',
        packageQuantity: Number(initialData.packageQuantity || 1),
        classBookingEnabled: Boolean(initialData.classBookingEnabled) || effectiveClassMeta.isClass,
        classParticipantIds: Array.isArray(initialData.classParticipantIds) ? initialData.classParticipantIds : [],
        classParticipantStatuses: initialData.classParticipantStatuses && typeof initialData.classParticipantStatuses === 'object' ? initialData.classParticipantStatuses : {},
        classCapacity: Number(initialData.classCapacity || effectiveClassMeta.classCapacity || 8),
        classNotificationsEnabled: initialData.classNotificationsEnabled !== false && effectiveClassMeta.classNotificationsEnabled !== false,
        repeatEnabled: Boolean(initialData.repeatEnabled),
        repeatFrequency: initialData.repeatFrequency ?? 'weekly',
        repeatCount: initialData.repeatCount ?? 1,
        bookingDepositEnabled: Boolean(initialData.bookingDepositEnabled),
        bookingDepositAmount: Number(initialData.bookingDepositAmount ?? 0),
        paymentHoldEnabled: Boolean(initialData.paymentHoldEnabled),
        paymentHoldReference: initialData.paymentHoldReference ?? '',
      };
      setPending(nextPending);
      setPatientQuery(allPatientOptions.find((p) => p.id === nextPending.patientId)?.name || '');
      setProviderQuery(providerOptions.find((p) => p.id === nextPending.providerId)?.name || '');
      fetchPatientDetail(nextPending.patientId);
    } else {
      const firstType = appointmentTypes[0];
      const firstVirtualMeta = virtualMetaForType(firstType);
      const firstClassMeta = classMetaForType(firstType);
      const firstTimingMeta = timingMetaForType(firstType);
      setPending({
        id: undefined,
        encounterId: null,
        patientId: '',
        providerId: providerOptions[0]?.id || '',
        status: 'SCHEDULED',
        appointmentTypeId: firstType?.id || '',
        billingServiceId: serviceVariantsEnabled ? firstType?.serviceVariants?.find((variant) => variant.active !== false)?.id || '' : '',
        locationId: defaultLocationId,
        start: new Date().toISOString().slice(0, 16),
        durationMinutes: firstTimingMeta.effectiveDurationMinutes || firstType?.durationMinutes || defaultSlotMinutes,
        title: '',
        isTelemed: firstType?.isTelemed || firstVirtualMeta.virtualDeliveryMode !== 'in_person',
        generateTelemedLink: (firstType?.isTelemed || firstVirtualMeta.virtualDeliveryMode !== 'in_person') && firstVirtualMeta.autoCreateVideoSession,
        joinUrl: null,
        roomResourceId: '',
        assetResourceIds: equipmentIdsForType(firstType),
        additionalStaffIds: [],
        medicalFormTemplateIds: [],
        emailReminderEnabled: false,
        smsReminderEnabled: false,
        reminderMinutesBefore: 1440,
        multiServiceIds: [],
        packageBookingEnabled: false,
        packageId: '',
        packageQuantity: 1,
        classBookingEnabled: firstClassMeta.isClass,
        classParticipantIds: [],
        classParticipantStatuses: {},
        classCapacity: firstClassMeta.classCapacity,
        classNotificationsEnabled: firstClassMeta.classNotificationsEnabled,
        repeatEnabled: false,
        repeatFrequency: 'weekly',
        repeatCount: 1,
        bookingDepositEnabled: false,
        bookingDepositAmount: 0,
        paymentHoldEnabled: false,
        paymentHoldReference: '',
      });
      setPatientQuery('');
      setProviderQuery(providerOptions[0]?.name || '');
      setPatientDetail(null);
    }
    setMedicalFormQuery('');
    setShowMedicalFormResults(false);
    setError(null);
    setNotice(null);
    setQuickActionsOpen(false);
  }, [isOpen, initialData, providerOptions, appointmentTypes, defaultSlotMinutes, allPatientOptions, defaultLocationId, serviceVariantsEnabled]);

  useEffect(() => {
    if (!quickActionsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!quickActionsRef.current) return;
      if (event.target instanceof Node && quickActionsRef.current.contains(event.target)) return;
      setQuickActionsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [quickActionsOpen]);

  const filteredPatients = useMemo(() => {
    const q = patientQuery.toLowerCase().trim();
    if (!q) return allPatientOptions;
    return allPatientOptions.filter((p) => {
      const name = p.name.toLowerCase();
      const mrn = String(p.mrn ?? '').toLowerCase();
      const phone = String(p.phone ?? '').toLowerCase();
      return name.includes(q) || mrn.includes(q) || phone.includes(q);
    });
  }, [patientQuery, allPatientOptions]);

  const filteredProviders = useMemo(() => {
    const q = providerQuery.toLowerCase().trim();
    if (!q) return providerOptions;
    return providerOptions.filter((p) => p.name.toLowerCase().includes(q));
  }, [providerQuery, providerOptions]);

  const selectedType = useMemo(
    () => appointmentTypes.find((t) => t.id === pending.appointmentTypeId),
    [appointmentTypes, pending.appointmentTypeId],
  );
  const selectedServiceOptions = useMemo(
    () => (selectedType?.serviceVariants || []).filter((variant) => variant.active !== false),
    [selectedType],
  );
  const selectedService = useMemo(
    () => selectedServiceOptions.find((variant) => variant.id === pending.billingServiceId) || null,
    [pending.billingServiceId, selectedServiceOptions],
  );
  const filteredAssetOptions = useMemo(
    () =>
      assetOptions
        .filter((asset) => asset.id !== pending.roomResourceId)
        .filter((asset) => !pending.locationId || !asset.locationId || asset.locationId === pending.locationId),
    [assetOptions, pending.locationId, pending.roomResourceId],
  );
  const filteredRoomOptions = useMemo(
    () => roomOptions.filter((room) => !pending.locationId || !room.locationId || room.locationId === pending.locationId),
    [pending.locationId, roomOptions],
  );
  const requiredEquipmentIds = useMemo(() => {
    return equipmentIdsForType(selectedType);
  }, [selectedType]);
  const selectedVirtualMeta = useMemo(() => virtualMetaForType(selectedType), [selectedType]);
  const selectedClassMeta = useMemo(() => classMetaForType(selectedType), [selectedType]);
  const selectedLocationName = locationOptions.find((location) => location.id === pending.locationId)?.name;
  const existingAttachedMedicalForms = useMemo<AppointmentMedicalForm[]>(
    () => (Array.isArray(initialData?.medicalForms) ? initialData.medicalForms : []),
    [initialData],
  );
  const lockedMedicalFormTemplateIds = useMemo(
    () => new Set(existingAttachedMedicalForms.map((item) => item.templateId)),
    [existingAttachedMedicalForms],
  );
  const filteredMedicalFormOptions = useMemo(() => {
    const query = medicalFormQuery.toLowerCase().trim();
    return medicalFormOptions
      .filter((option) => !pending.medicalFormTemplateIds.includes(option.id))
      .filter((option) => {
        if (!query) return true;
        return [option.name, option.code || '', option.category || ''].some((value) => value.toLowerCase().includes(query));
      })
      .slice(0, 10);
  }, [medicalFormOptions, medicalFormQuery, pending.medicalFormTemplateIds]);
  const selectedMedicalForms = useMemo(
    () =>
      pending.medicalFormTemplateIds.map((templateId) => {
        const option = medicalFormOptions.find((item) => item.id === templateId);
        const attached = existingAttachedMedicalForms.find((item) => item.templateId === templateId);
        return {
          templateId,
          templateName: option?.name || attached?.templateName || tr('Medical form'),
          code: option?.code || null,
          category: option?.category || null,
          status: attached?.status || null,
          isLocked: lockedMedicalFormTemplateIds.has(templateId),
        };
      }),
    [existingAttachedMedicalForms, lockedMedicalFormTemplateIds, medicalFormOptions, pending.medicalFormTemplateIds, tr],
  );
  const isTelemedType = Boolean(selectedType?.isTelemed);
  const translateAppointmentTypeName = (name?: string | null) => (name ? tr(name) : tr('Appointment'));

  useEffect(() => {
    if (!serviceVariantsEnabled) {
      if (!pending.billingServiceId) return;
      setPending((prev) => ({
        ...prev,
        billingServiceId: '',
      }));
      return;
    }
    if (!selectedType) return;
    const availableServices = (selectedType.serviceVariants || []).filter((variant) => variant.active !== false);
    const hasSelectedService = availableServices.some((variant) => variant.id === pending.billingServiceId);
    const nextBillingServiceId = hasSelectedService ? pending.billingServiceId || '' : availableServices[0]?.id || '';
    if ((pending.billingServiceId || '') === nextBillingServiceId) return;
    setPending((prev) => ({
      ...prev,
      billingServiceId: nextBillingServiceId,
    }));
  }, [pending.billingServiceId, selectedType, serviceVariantsEnabled]);

  const appointmentMarker = pending.id ? `${APPOINTMENT_NOTE_MARKER_PREFIX}${pending.id}` : '';

  const buildAppointmentNoteTitle = () => {
    const when = pending.start ? new Date(pending.start) : null;
    const dateLabel = when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString(locale, { dateStyle: 'medium' }) : tr('Unknown date');
    return `${tr('Encounter Note')} - ${dateLabel}`;
  };

  const escapeHtml = (value: string) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const buildAppointmentNoteHtml = () => {
    const providerName = providerOptions.find((provider) => provider.id === pending.providerId)?.name || tr('Unassigned provider');
    const when = pending.start ? new Date(pending.start) : null;
    const whenLabel =
      when && !Number.isNaN(when.getTime())
        ? when.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
        : tr('Unknown date/time');
    const appointmentTypeName = translateAppointmentTypeName(selectedType?.name);
    const reason = pending.title?.trim() || tr('No reason entered');
    return `
      <h3>${tr('Encounter Context')}</h3>
      <p><strong>${tr('Appointment time')}:</strong> ${escapeHtml(whenLabel)}</p>
      <p><strong>${tr('Appointment Type')}:</strong> ${escapeHtml(appointmentTypeName)}</p>
      <p><strong>${tr('Provider')}:</strong> ${escapeHtml(providerName)}</p>
      <p><strong>${tr('Reason')}:</strong> ${escapeHtml(reason)}</p>
      <p><strong>${tr('Linked appointment')}:</strong> ${escapeHtml(appointmentMarker)}</p>
      <hr/>
      <h3>${tr('Subjective')}</h3><p></p>
      <h3>${tr('Objective')}</h3><p></p>
      <h3>${tr('Assessment')}</h3><p></p>
      <h3>${tr('Plan')}</h3><p></p>
    `;
  };

  const findLinkedAppointmentNote = async (): Promise<PatientNote | null> => {
    if (!pending.patientId || !pending.id) return null;
    const token = await getToken();
    if (!token) return null;
    const marker = `${APPOINTMENT_NOTE_MARKER_PREFIX}${pending.id}`;
    const res = await fetch(`${API_BASE}/patients/${pending.patientId}/notes?q=${encodeURIComponent(marker)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const notes = (await res.json()) as PatientNote[];
    return (
      notes.find(
        (note) =>
          note.contentText?.includes(marker) ||
          note.contentHtml?.includes(marker) ||
          note.title?.includes(marker),
      ) || null
    );
  };

  const fetchPatientDetail = async (id: string) => {
    if (!id) {
      setPatientDetail(null);
      return;
    }
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE}/patients/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const data = await res.json();
      setPatientDetail(data);
    } catch {
      setPatientDetail(null);
    }
  };

  const openCreatePatientDialog = () => {
    setCreatePatientForm(emptyCreatePatientForm());
    setCreatePatientError(null);
    setShowCreatePatientDialog(true);
  };

  const closeCreatePatientDialog = () => {
    if (createPatientSaving) return;
    setShowCreatePatientDialog(false);
    setCreatePatientError(null);
  };

  const createAndSelectPatient = async () => {
    setCreatePatientError(null);
    if (!createPatientForm.firstName.trim() || !createPatientForm.lastName.trim() || !createPatientForm.dob || !createPatientForm.mrn.trim()) {
      setCreatePatientError(tr('First name, last name, DOB, and MRN are required'));
      return;
    }

    setCreatePatientSaving(true);
    try {
      const token = await getToken();
      if (!token) throw new Error(tr('Unable to authenticate request'));

      const payload = {
        mrn: createPatientForm.mrn.trim(),
        firstName: createPatientForm.firstName.trim(),
        lastName: createPatientForm.lastName.trim(),
        dob: createPatientForm.dob,
        sex: createPatientForm.sex || undefined,
        phone: createPatientForm.phone.trim() || undefined,
        email: createPatientForm.email.trim() || undefined,
        status: 'ACTIVE',
        allergies: [],
        problems: [],
        medications: [],
        pastMedicalHistory: [],
        surgeries: [],
        familyHistory: [],
        socialHistory: [],
      };
      const res = await fetch(`${API_BASE}/patients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`${tr('Create patient')} ${tr('failed')} ${res.status}`);
      const created = await res.json();
      const createdOption: PatientOption = {
        id: created.id,
        name: `${created.firstName ?? payload.firstName} ${created.lastName ?? payload.lastName}`.trim() || payload.mrn,
        avatarUrl: created.avatarUrl ?? created.photoUrl ?? null,
        mrn: created.mrn ?? payload.mrn,
        phone: created.phone ?? payload.phone ?? null,
        dob: created.dob ?? payload.dob,
      };

      setCreatedPatientOptions((prev) => [createdOption, ...prev.filter((patient) => patient.id !== createdOption.id)]);
      setPending((prev) => ({ ...prev, patientId: createdOption.id }));
      setPatientQuery(createdOption.name);
      setPatientDetail({ ...created, ...payload, id: createdOption.id });
      setShowPatientResults(false);
      setShowCreatePatientDialog(false);
      setCreatePatientForm(emptyCreatePatientForm());
    } catch (err: any) {
      setCreatePatientError(err.message || tr('Failed to save patient'));
    } finally {
      setCreatePatientSaving(false);
    }
  };

  useEffect(() => {
    let active = true;
    const hydrateLinkedNote = async () => {
      if (!isOpen || !pending.patientId || !pending.id) {
        setAppointmentNoteId(null);
        return;
      }
      try {
        const existing = await findLinkedAppointmentNote();
        if (!active) return;
        setAppointmentNoteId(existing?.id || null);
      } catch {
        if (!active) return;
        setAppointmentNoteId(null);
      }
    };
    void hydrateLinkedNote();
    return () => {
      active = false;
    };
  }, [isOpen, pending.patientId, pending.id]);

  const openOrCreateAppointmentNote = async () => {
    if (!pending.patientId || !pending.id) return;
    setError(null);
    setNotice(null);
    setNoteActionBusy(true);
    try {
      const token = await getToken();
      if (!token) return;

      let noteId = appointmentNoteId;
      if (!noteId) {
        const existing = await findLinkedAppointmentNote();
        if (existing?.id) {
          noteId = existing.id;
          setAppointmentNoteId(existing.id);
        }
      }

      if (!noteId) {
        const res = await fetch(`${API_BASE}/patients/${pending.patientId}/notes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: buildAppointmentNoteTitle(),
            contentHtml: buildAppointmentNoteHtml(),
            pinned: false,
          }),
        });
        if (!res.ok) {
          throw new Error(`Failed to create note (${res.status})`);
        }
        const created = (await res.json()) as PatientNote;
        noteId = created.id;
        setAppointmentNoteId(created.id);
      }

      const nextQuery = new URLSearchParams({
        tab: 'Notes',
        appointmentId: pending.id,
      });
      if (noteId) nextQuery.set('noteId', noteId);
      navigate(`/patients/${pending.patientId}?${nextQuery.toString()}`);
      onClose();
    } catch (err: any) {
      setError(err?.message || tr('Failed to open encounter note.'));
    } finally {
      setNoteActionBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!pending.patientId || !pending.providerId || !pending.appointmentTypeId || !pending.locationId) {
      setError(tr('Patient, provider, appointment type, and location are required'));
      return;
    }
    try {
      setSaving(true);
      setNotice(null);
      await onSubmit(pending);
      onClose();
    } catch (err: any) {
      setError(err.message || tr('Failed to save appointment'));
    } finally {
      setSaving(false);
    }
  };

  const canStartVisitFromModal = Boolean(
    pending.id && (pending.encounterId || !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(pending.status)),
  );

  const runAppointmentAutomation = async (
    action: 'schedule-reminders' | 'care-tasks' | 'check-in' | 'no-show',
  ) => {
    if (!pending.id) return;
    setError(null);
    setNotice(null);
    setAutomationBusyAction(action);
    try {
      const token = await getToken();
      if (!token) throw new Error(tr('Unable to authenticate request'));

      const endpoint =
        action === 'schedule-reminders'
          ? `${API_BASE}/scheduling-automation/appointments/${pending.id}/reminders/schedule`
          : action === 'care-tasks'
            ? `${API_BASE}/scheduling-automation/appointments/${pending.id}/care-tasks/defaults`
            : `${API_BASE}/scheduling-automation/appointments/${pending.id}/lifecycle`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(action === 'schedule-reminders' || action === 'care-tasks' ? {} : { 'Content-Type': 'application/json' }),
        },
        body:
          action === 'schedule-reminders' || action === 'care-tasks'
            ? undefined
            : JSON.stringify({ action }),
      });

      if (!response.ok) {
        throw new Error((await response.text()) || tr('Appointment action failed.'));
      }

      if (action === 'check-in') {
        setPending((prev) => ({ ...prev, status: 'CHECKED_IN' }));
        setNotice(tr('Appointment checked in.'));
      } else if (action === 'no-show') {
        setPending((prev) => ({ ...prev, status: 'NO_SHOW' }));
        setNotice(tr('Appointment marked as no-show.'));
      } else if (action === 'schedule-reminders') {
        setNotice(tr('Reminders scheduled.'));
      } else {
        setNotice(tr('Care tasks created.'));
      }

      if (pending.id) {
        await onAppointmentActionComplete?.(pending.id);
      }
    } catch (err: any) {
      setError(err?.message || tr('Appointment action failed.'));
    } finally {
      setAutomationBusyAction(null);
    }
  };

  const startOrOpenVisit = async () => {
    if (!pending.id) return;
    setError(null);
    setNotice(null);
    setVisitActionBusy(true);
    try {
      if (pending.encounterId) {
        onClose();
        navigate(`/visits/${pending.encounterId}`);
        return;
      }

      const token = await getToken();
      if (!token) throw new Error(tr('Unable to authenticate request'));

      const response = await fetch(`${API_BASE}/encounters/start-from-appointment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ appointmentId: pending.id }),
      });

      if (!response.ok) {
        throw new Error((await response.text()) || tr('Unable to start visit.'));
      }

      const result = (await response.json()) as { encounterId: string };
      setPending((prev) => ({ ...prev, encounterId: result.encounterId }));
      await onAppointmentActionComplete?.(pending.id);
      onClose();
      navigate(`/visits/${result.encounterId}`);
    } catch (err: any) {
      setError(err?.message || tr('Unable to start visit.'));
    } finally {
      setVisitActionBusy(false);
    }
  };

  const toLocalInput = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const loadSmartSuggestions = async () => {
    if (!pending.patientId || !pending.appointmentTypeId) {
      setError(tr('Patient and appointment type are required for smart suggestions.'));
      return;
    }
    const selectedStart = pending.start ? new Date(pending.start) : new Date();
    const start = Number.isNaN(selectedStart.getTime()) ? new Date() : selectedStart;
    const end = new Date(start);
    end.setDate(end.getDate() + 14);
    const startDate = new Date(start.getTime() - start.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const endDate = new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    try {
      setSmartSuggestBusy(true);
      setError(null);
      const token = await getToken();
      const params = new URLSearchParams({
        patientId: pending.patientId,
        appointmentTypeId: pending.appointmentTypeId,
        startDate,
        endDate,
        limit: '5',
      });
      const response = await fetch(`${API_BASE}/scheduling-automation/assistant/suggestions?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error(`Smart suggestions failed (${response.status})`);
      const payload = (await response.json()) as { data?: SmartSuggestion[] };
      setSmartSuggestions(Array.isArray(payload.data) ? payload.data : []);
    } catch (err: any) {
      setError(err?.message || tr('Unable to load smart suggestions.'));
    } finally {
      setSmartSuggestBusy(false);
    }
  };

  const applySmartSuggestion = (suggestion: SmartSuggestion) => {
    const provider = providerOptions.find((item) => item.id === suggestion.providerUserId);
    setPending((prev) => ({
      ...prev,
      providerId: suggestion.providerUserId,
      start: toLocalInput(suggestion.startTime),
      durationMinutes: suggestion.durationMinutes,
    }));
    setProviderQuery(provider?.name || suggestion.providerName);
  };

  const handleTypeChange = (newTypeId: string) => {
    const match = appointmentTypes.find((t) => t.id === newTypeId);
    const firstServiceId = serviceVariantsEnabled ? match?.serviceVariants?.find((variant) => variant.active !== false)?.id || '' : '';
    const requiredEquipment = equipmentIdsForType(match);
    const virtualMeta = virtualMetaForType(match);
    const classMeta = classMetaForType(match);
    const timingMeta = timingMetaForType(match);
    const isVirtual = Boolean(match?.isTelemed) || virtualMeta.virtualDeliveryMode !== 'in_person';
    setPending((prev) => ({
      ...prev,
      appointmentTypeId: newTypeId,
      billingServiceId: firstServiceId,
      durationMinutes: match ? timingMeta.effectiveDurationMinutes : defaultSlotMinutes,
      isTelemed: isVirtual,
      generateTelemedLink: isVirtual ? virtualMeta.autoCreateVideoSession : false,
      assetResourceIds: Array.from(new Set([...prev.assetResourceIds, ...requiredEquipment])),
      classBookingEnabled: classMeta.isClass,
      classCapacity: classMeta.classCapacity,
      classNotificationsEnabled: classMeta.classNotificationsEnabled,
      classParticipantIds: classMeta.isClass ? prev.classParticipantIds.slice(0, Math.max(0, classMeta.classCapacity - 1)) : [],
      classParticipantStatuses: classMeta.isClass ? prev.classParticipantStatuses : {},
    }));
  };

  const toggleClassParticipant = (patientId: string) => {
    setPending((prev) => {
      const exists = prev.classParticipantIds.includes(patientId);
      if (exists) {
        const nextStatuses = { ...prev.classParticipantStatuses };
        delete nextStatuses[patientId];
        return { ...prev, classParticipantIds: prev.classParticipantIds.filter((id) => id !== patientId), classParticipantStatuses: nextStatuses };
      }
      const maxAdditional = Math.max(0, prev.classCapacity - 1);
      if (prev.classParticipantIds.length >= maxAdditional) return prev;
      return {
        ...prev,
        classParticipantIds: [...prev.classParticipantIds, patientId],
        classParticipantStatuses: { ...prev.classParticipantStatuses, [patientId]: 'SCHEDULED' },
      };
    });
  };

  const updateClassParticipantStatus = (patientId: string, status: AppointmentStatus) => {
    setPending((prev) => ({
      ...prev,
      classParticipantStatuses: { ...prev.classParticipantStatuses, [patientId]: status },
    }));
  };

  const toggleAsset = (assetId: string) => {
    setPending((prev) => {
      const exists = prev.assetResourceIds.includes(assetId);
      return {
        ...prev,
        assetResourceIds: exists
          ? prev.assetResourceIds.filter((id) => id !== assetId)
          : [...prev.assetResourceIds, assetId],
      };
    });
  };

  const addMedicalForm = (templateId: string) => {
    setPending((prev) => {
      if (prev.medicalFormTemplateIds.includes(templateId)) return prev;
      return {
        ...prev,
        medicalFormTemplateIds: [...prev.medicalFormTemplateIds, templateId],
      };
    });
    setMedicalFormQuery('');
    setShowMedicalFormResults(false);
  };

  const removeMedicalForm = (templateId: string) => {
    if (lockedMedicalFormTemplateIds.has(templateId)) return;
    setPending((prev) => ({
      ...prev,
      medicalFormTemplateIds: prev.medicalFormTemplateIds.filter((item) => item !== templateId),
    }));
  };

  const patientName = patientDetail
    ? `${patientDetail.firstName ?? ''} ${patientDetail.lastName ?? ''}`.trim() || tr('Patient')
    : patientQuery || tr('Patient');
  const patientAge =
    patientDetail?.age ??
    (patientDetail?.dob
      ? Math.max(0, Math.floor((Date.now() - new Date(patientDetail.dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25)))
      : null);
  const patientGender = patientDetail?.sex || patientDetail?.gender;
  const patientMetaLine = [
    patientDetail?.mrn ? `MRN ${patientDetail.mrn}` : null,
    patientAge !== null && patientAge !== undefined ? `${patientAge} ${tr('yrs')}` : null,
    patientDetail?.dob ? `${tr('DOB')} ${new Date(patientDetail.dob).toLocaleDateString(locale)}` : null,
    patientGender ? patientGender : null,
  ]
    .filter(Boolean)
    .join(' | ');

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-800">{pending.id ? tr('Edit Appointment') : tr('Request Appointment')}</h2>
            {pending.id && (
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium border border-slate-200">
                #{pending.id.slice(-6).toUpperCase()}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Plus size={24} className="rotate-45" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/40">
          {error && <div className="text-sm text-rose-600 bg-rose-50 p-3 rounded-lg border border-rose-100">{error}</div>}
          {notice && <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100">{notice}</div>}

          <section className="bg-white rounded-2xl p-2 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div>
                  <h4 className="font-semibold text-slate-800">{tr('Select a Patient')}</h4>
                  <p className="text-xs text-slate-500">{tr('Search for an existing patient to schedule this appointment.')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={openCreatePatientDialog}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <UserPlus size={14} />
                {tr('Create patient')}
              </button>
            </div>

            <div className="space-y-2">
              <div className="relative">
                {pending.patientId ? (
                  <div className="flex min-h-[38px] items-center rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                      <span className="truncate">{patientName}</span>
                      {patientMetaLine ? (
                        <span className="max-w-[220px] truncate text-[10px] font-medium text-emerald-700/70">{patientMetaLine}</span>
                      ) : null}
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-lg leading-none text-emerald-700/80 hover:bg-emerald-100 hover:text-emerald-900"
                        onClick={() => {
                          setPending((prev) => ({ ...prev, patientId: '' }));
                          setPatientQuery('');
                          setPatientDetail(null);
                          setShowPatientResults(false);
                        }}
                        aria-label={tr('Change patient')}
                      >
                        ×
                      </button>
                    </span>
                  </div>
                ) : (
                  <>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      autoFocus
                      className={`${fieldClass} pl-9 placeholder:text-xs`}
                      placeholder={tr('Search by name, MRN, or phone...')}
                      value={patientQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPatientQuery(val);
                        setShowPatientResults(val.trim().length > 0);
                      }}
                      onFocus={() => setShowPatientResults(patientQuery.trim().length > 0)}
                      onBlur={() => {
                        window.setTimeout(() => setShowPatientResults(false), 120);
                      }}
                    />

                    {showPatientResults && patientQuery.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[260px] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                        {filteredPatients.length > 0 ? (
                          filteredPatients.map((p) => {
                            const initials = p.name
                              .split(' ')
                              .filter(Boolean)
                              .map((part) => part[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase();
                            const rightMeta = [p.mrn ? `MRN ${p.mrn}` : null, p.phone || null]
                              .filter(Boolean)
                              .join(' • ');
                            return (
                              <button
                                key={p.id}
                                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-slate-50"
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  setPending((prev) => ({ ...prev, patientId: p.id }));
                                  setPatientQuery('');
                                  setShowPatientResults(false);
                                  fetchPatientDetail(p.id);
                                }}
                              >
                                <InitialAvatar name={p.name} src={p.avatarUrl} alt={p.name} className="h-7 w-7 text-[10px]" />
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-medium text-slate-700">{p.name}</span>
                                  {p.dob ? (
                                    <span className="block truncate text-[11px] text-slate-500">
                                      {tr('DOB')} {new Date(p.dob).toLocaleDateString(locale)}
                                    </span>
                                  ) : null}
                                </div>
                                <span className="max-w-[40%] truncate text-[11px] text-slate-500">{rightMeta}</span>
                              </button>
                            );
                          })
                        ) : (
                          <div className="p-3 text-sm text-slate-500">{tr('No patients found.')}</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-lime-200 bg-lime-50/60 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {tr('Find the perfect slot with Booking Wizard')}
                </p>
                <p className="text-xs text-slate-600">
                  {tr(
                    'Booking Wizard uses AI to scan staff availability, service duration, room requirements, and client history to suggest the best available slots, while making rooms and equipment bookable to prevent double bookings.'
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={loadSmartSuggestions}
                disabled={smartSuggestBusy || !pending.patientId || !pending.appointmentTypeId}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {smartSuggestBusy ? tr('Scanning...') : tr('Booking Wizard')}
              </button>
            </div>
            {smartSuggestions.length ? (
              <div className="mt-3 grid gap-2">
                {smartSuggestions.map((suggestion) => (
                  <button
                    key={`${suggestion.providerUserId}:${suggestion.startTime}`}
                    type="button"
                    onClick={() => applySmartSuggestion(suggestion)}
                    className="rounded-xl border border-lime-200 bg-white px-3 py-2 text-left transition hover:border-lime-400"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">
                        {new Date(suggestion.startTime).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                      <span className="rounded-full bg-lime-100 px-2 py-1 text-[11px] font-bold text-lime-800">
                        {tr('Score')} {suggestion.score}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-600">
                      {suggestion.providerName} · {suggestion.appointmentTypeName}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">{suggestion.reasons?.slice(0, 2).join(' ')}</p>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="bg-white rounded-2xl p-2 space-y-4">
            <div className="flex items-center gap-2">
              <div>
                <h4 className="font-semibold text-slate-800">{tr('Appointment Details')}</h4>
                <p className="text-xs text-slate-500">{tr('Date, time, type, provider and status')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <CalendarDays size={14} /> {tr('Date & Time')}
                </label>
                <input
                  type="datetime-local"
                  className={fieldClass}
                  value={pending.start}
                  onChange={(e) => setPending({ ...pending, start: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <Clock3 size={14} /> {tr('Appointment Type')}
                </label>
                <select className={fieldClass} value={pending.appointmentTypeId} onChange={(e) => handleTypeChange(e.target.value)}>
                  {appointmentTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {translateAppointmentTypeName(t.name)} ({t.durationMinutes} {tr('min')})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-600">{tr('Multiple Service Booking')}</p>
                    <p className="text-[11px] text-slate-500">{tr('Select additional services to book in the same flow.')}</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
                    <input
                      type="checkbox"
                      checked={pending.packageBookingEnabled}
                      onChange={(event) => setPending((prev) => ({ ...prev, packageBookingEnabled: event.target.checked }))}
                    />
                    {tr('Package Appointment Booking')}
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  {appointmentTypes.slice(0, 10).map((type) => {
                    const selected = pending.multiServiceIds.includes(type.id);
                    const isPrimary = pending.appointmentTypeId === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        disabled={isPrimary}
                        className={cn(
                          'rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50',
                          selected || isPrimary ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                        )}
                        onClick={() =>
                          setPending((prev) => ({
                            ...prev,
                            multiServiceIds: selected
                              ? prev.multiServiceIds.filter((id) => id !== type.id)
                              : [...prev.multiServiceIds, type.id],
                          }))
                        }
                      >
                        {type.name}
                        {isPrimary ? ` · ${tr('Primary')}` : ''}
                      </button>
                    );
                  })}
                </div>
                {pending.packageBookingEnabled ? (
                  <div className="grid gap-2 md:grid-cols-[1fr_120px]">
                    <input
                      className={fieldClass}
                      placeholder={tr('Client package ID or package reference')}
                      value={pending.packageId}
                      onChange={(event) => setPending((prev) => ({ ...prev, packageId: event.target.value }))}
                    />
                    <input
                      type="number"
                      min={1}
                      className={fieldClass}
                      value={pending.packageQuantity}
                      onChange={(event) => setPending((prev) => ({ ...prev, packageQuantity: Math.max(1, Number(event.target.value || 1)) }))}
                      title={tr('Sessions to redeem')}
                    />
                  </div>
                ) : null}
              </div>

              {serviceVariantsEnabled ? (
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                    <FileText size={14} /> {tr('Service option')}
                  </label>
                  <select
                    className={fieldClass}
                    value={pending.billingServiceId || ''}
                    onChange={(e) => setPending((prev) => ({ ...prev, billingServiceId: e.target.value }))}
                    disabled={!selectedServiceOptions.length}
                  >
                    <option value="">{selectedServiceOptions.length ? tr('Use appointment type only') : tr('No linked services')}</option>
                    {selectedServiceOptions.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name}
                        {variant.category ? ` - ${variant.category}` : ''}
                        {Number.isFinite(Number(variant.price)) ? ` ($${Number(variant.price).toFixed(2)})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500">
                    {selectedService
                      ? tr('The booked treatment or service option that will be carried into completion and billing.')
                      : tr('Use the appointment type on its own when no specific service option needs to be booked yet.')}
                  </p>
                </div>
              ) : null}

              {pending.isTelemed ? (
                <div className="space-y-3 rounded-xl border border-sky-100 bg-sky-50 p-3 md:col-span-2">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-sky-950">{tr('Video Conferencing')}</p>
                      <p className="text-xs text-sky-800">
                        {selectedVirtualMeta.virtualDeliveryMode === 'hybrid'
                          ? tr('This service can be delivered in person or online.')
                          : tr('This appointment will be delivered online.')}
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-sky-800">
                      {selectedVirtualMeta.groupVideoEnabled ? tr('Group video') : tr('One-to-one video')}
                    </span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="flex items-start gap-2 rounded-lg border border-sky-100 bg-white px-3 py-2 text-sm text-slate-700 md:col-span-1">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={pending.generateTelemedLink !== false}
                        onChange={(event) => setPending((prev) => ({ ...prev, generateTelemedLink: event.target.checked }))}
                      />
                      <span>
                        <span className="block font-semibold">{tr('Create video link')}</span>
                        <span className="block text-xs text-slate-500">{tr('Generate the built-in telemedicine room after booking.')}</span>
                      </span>
                    </label>
                    <div className="rounded-lg border border-sky-100 bg-white px-3 py-2 text-sm">
                      <span className="block text-xs font-semibold text-slate-500">{tr('Provider')}</span>
                      <span className="font-medium text-slate-800">{selectedVirtualMeta.videoProviderOverride || tr('Global telemedicine provider')}</span>
                    </div>
                    <div className="rounded-lg border border-sky-100 bg-white px-3 py-2 text-sm">
                      <span className="block text-xs font-semibold text-slate-500">{tr('Max participants')}</span>
                      <span className="font-medium text-slate-800">{selectedVirtualMeta.maxVideoParticipants}</span>
                    </div>
                  </div>
                  {selectedVirtualMeta.videoConsultationInstructions || selectedVirtualMeta.videoTroubleshootingFaq ? (
                    <div className="grid gap-3 md:grid-cols-2">
                      {selectedVirtualMeta.videoConsultationInstructions ? (
                        <div className="rounded-lg border border-sky-100 bg-white px-3 py-2 text-sm">
                          <span className="block text-xs font-semibold text-slate-500">{tr('Video Consultation Support')}</span>
                          <p className="mt-1 whitespace-pre-wrap text-slate-700">{selectedVirtualMeta.videoConsultationInstructions}</p>
                        </div>
                      ) : null}
                      {selectedVirtualMeta.videoTroubleshootingFaq ? (
                        <div className="rounded-lg border border-sky-100 bg-white px-3 py-2 text-sm">
                          <span className="block text-xs font-semibold text-slate-500">{tr('FAQ / troubleshooting')}</span>
                          <p className="mt-1 whitespace-pre-wrap text-slate-700">{selectedVirtualMeta.videoTroubleshootingFaq}</p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {pending.classBookingEnabled ? (
                <div className="space-y-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3 md:col-span-2">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-emerald-950">{tr('Class Booking')}</p>
                      <p className="text-xs text-emerald-800">{tr('Add or remove class participants while booking from the calendar.')}</p>
                    </div>
                    <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-emerald-800">
                      {1 + pending.classParticipantIds.length}/{pending.classCapacity} {tr('participants')}
                    </span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[120px_1fr]">
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-600">{tr('Capacity')}</span>
                      <input
                        type="number"
                        min={1}
                        className={fieldClass}
                        value={pending.classCapacity}
                        onChange={(event) => setPending((prev) => ({
                          ...prev,
                          classCapacity: Math.max(1, Number(event.target.value) || 1),
                          classParticipantIds: prev.classParticipantIds.slice(0, Math.max(0, (Number(event.target.value) || 1) - 1)),
                        }))}
                      />
                    </label>
                    <label className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={pending.classNotificationsEnabled}
                        onChange={(event) => setPending((prev) => ({ ...prev, classNotificationsEnabled: event.target.checked }))}
                      />
                      <span>
                        <span className="block font-semibold">{tr('Class Notifications')}</span>
                        <span className="block text-xs text-slate-500">{tr('Send confirmation and reminder notifications to class participants.')}</span>
                      </span>
                    </label>
                  </div>
                  <div className="rounded-lg border border-emerald-100 bg-white p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-600">{tr('Class Participant Management')}</span>
                      {selectedClassMeta.classWaitlistEnabled ? <span className="text-xs font-semibold text-amber-700">{tr('Waitlist enabled')}</span> : null}
                    </div>
                    <div className="grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
                      {allPatientOptions
                        .filter((patient) => patient.id !== pending.patientId)
                        .slice(0, 24)
                        .map((patient) => {
                          const selected = pending.classParticipantIds.includes(patient.id);
                          const full = !selected && pending.classParticipantIds.length >= Math.max(0, pending.classCapacity - 1);
                          return (
                            <div
                              key={patient.id}
                              className={`rounded-lg border px-3 py-2 text-sm transition ${
                                selected ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <button
                                type="button"
                                disabled={full}
                                onClick={() => toggleClassParticipant(patient.id)}
                                className="block w-full text-left disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <span className="block truncate font-medium">{patient.name}</span>
                                <span className="block truncate text-xs opacity-75">{patient.phone || patient.mrn || tr('Client')}</span>
                              </button>
                              {selected ? (
                                <select
                                  className="mt-2 w-full rounded-md border border-emerald-200 bg-white px-2 py-1 text-xs"
                                  value={pending.classParticipantStatuses[patient.id] || 'SCHEDULED'}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) => updateClassParticipantStatus(patient.id, event.target.value as AppointmentStatus)}
                                >
                                  {(Object.keys(statusLabels) as AppointmentStatus[]).map((status) => (
                                    <option key={status} value={status}>{tr(statusLabels[status])}</option>
                                  ))}
                                </select>
                              ) : null}
                            </div>
                          );
                        })}
                    </div>
                    {selectedClassMeta.classParticipantInstructions ? (
                      <p className="mt-3 whitespace-pre-wrap rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">{selectedClassMeta.classParticipantInstructions}</p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <MapPin size={14} /> {tr('Location')}
                </label>
                <div className="relative min-h-[38px] rounded-lg border border-slate-200 bg-white px-2 py-1.5 pr-9 focus-within:ring-2 focus-within:ring-ink/10">
                  <div className="flex min-h-[24px] items-center">
                    {selectedLocationName ? (
                      <span className="max-w-[calc(100%-8px)] truncate rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                        {selectedLocationName}
                      </span>
                    ) : (
                      <span className="px-1 text-sm text-slate-400">{tr('Select location')}</span>
                    )}
                  </div>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                  />
                  <select
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    aria-label={tr('Location')}
                    value={pending.locationId}
                    onChange={(e) =>
                      setPending((prev) => ({
                        ...prev,
                        locationId: e.target.value,
                        roomResourceId: '',
                        assetResourceIds: requiredEquipmentIds,
                      }))
                    }
                  >
                    <option value="">{tr('Select location')}</option>
                    {locationOptions.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <MapPin size={14} /> {tr('Room')}
                </label>
                <select
                  className={fieldClass}
                  value={pending.roomResourceId || ''}
                  onChange={(e) => {
                    const roomResourceId = e.target.value;
                    setPending((prev) => ({
                      ...prev,
                      roomResourceId,
                      assetResourceIds: prev.assetResourceIds.filter((id) => id !== roomResourceId),
                    }));
                  }}
                >
                  <option value="">{tr('No room assigned')}</option>
                  {filteredRoomOptions.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name} ({tr(room.type)})
                    </option>
                  ))}
                </select>
                {!roomOptions.length ? (
                  <p className="text-xs text-slate-500">{tr('Add rooms in Settings > Schedule Assets. Resources with type Room, Suite, Bay, or Bed appear here.')}</p>
                ) : null}
              </div>

              <div className="space-y-1 relative">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <UserPlus size={14} /> {tr('Provider')}
                </label>
                {pending.providerId ? (
                  <div className="flex min-h-[38px] items-center rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                    <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      <span className="truncate">{providerQuery || providerOptions.find((p) => p.id === pending.providerId)?.name}</span>
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-lg leading-none text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                        onClick={() => {
                          setPending((prev) => ({ ...prev, providerId: '' }));
                          setProviderQuery('');
                          setShowProviderResults(false);
                        }}
                        aria-label={tr('Select provider')}
                      >
                        ×
                      </button>
                    </span>
                  </div>
                ) : (
                  <input
                    className={fieldClass}
                    placeholder={tr('Select provider')}
                    value={providerQuery}
                    onChange={(e) => {
                      setProviderQuery(e.target.value);
                      setPending((prev) => ({ ...prev, providerId: '' }));
                    }}
                    onFocus={() => setShowProviderResults(true)}
                  />
                )}
                {showProviderResults && !pending.providerId && (
                  <div className="absolute top-full left-0 w-full mt-2 bg-white rounded-lg shadow-xl border border-slate-100 z-20 max-h-48 overflow-y-auto">
                    {filteredProviders.map((p) => {
                      const providerInitials = p.name
                        .split(' ')
                        .filter(Boolean)
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase();
                      return (
                        <button
                          key={p.id}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2"
                          onMouseDown={() => {
                            setPending((prev) => ({ ...prev, providerId: p.id, additionalStaffIds: prev.additionalStaffIds.filter((id) => id !== p.id) }));
                            setProviderQuery(p.name);
                            setShowProviderResults(false);
                          }}
                        >
                          <InitialAvatar name={p.name} src={p.avatarUrl} alt={p.name} className="h-6 w-6 text-[10px]" />
                          <span>{p.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-1 md:col-span-2">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <UserPlus size={14} /> {tr('Multi-Staff Appointments')}
                </label>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    {providerOptions
                      .filter((provider) => provider.id !== pending.providerId)
                      .slice(0, 12)
                      .map((provider) => {
                        const selected = pending.additionalStaffIds.includes(provider.id);
                        return (
                          <button
                            key={provider.id}
                            type="button"
                            onClick={() =>
                              setPending((prev) => ({
                                ...prev,
                                additionalStaffIds: selected
                                  ? prev.additionalStaffIds.filter((id) => id !== provider.id)
                                  : [...prev.additionalStaffIds, provider.id],
                              }))
                            }
                            className={cn(
                              'rounded-full border px-3 py-1.5 text-xs font-semibold transition',
                              selected ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300',
                            )}
                          >
                            {provider.name}
                          </button>
                        );
                      })}
                    {providerOptions.filter((provider) => provider.id !== pending.providerId).length === 0 ? (
                      <span className="text-xs text-slate-500">{tr('No additional staff available')}</span>
                    ) : null}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{tr('Additional staff are captured with the booking notes for team visibility.')}</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full',
                      pending.status === 'SCHEDULED'
                        ? 'bg-sky-500'
                        : pending.status === 'PORTAL_BOOKED'
                        ? 'bg-lime-500'
                        : pending.status === 'CHECKED_IN'
                        ? 'bg-emerald-500'
                        : pending.status === 'COMPLETED'
                        ? 'bg-slate-500'
                        : pending.status === 'CANCELLED'
                        ? 'bg-rose-500'
                        : 'bg-amber-500',
                    )}
                  />
                  {tr('Status')}
                </label>
                <select
                  className={fieldClass}
                  value={pending.status}
                  onChange={(e) => setPending({ ...pending, status: e.target.value as AppointmentStatus })}
                >
                  {(Object.keys(statusLabels) as AppointmentStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {tr(statusLabels[s])}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <Clock3 size={14} /> {tr('Duration (minutes)')}
                </label>
                <input
                  type="number"
                  min={5}
                  className={fieldClass}
                  value={pending.durationMinutes}
                  onChange={(e) => setPending({ ...pending, durationMinutes: Number(e.target.value) })}
                />
              </div>

              <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3 md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={pending.repeatEnabled}
                    onChange={(event) => setPending((prev) => ({ ...prev, repeatEnabled: event.target.checked }))}
                  />
                  {tr('Repeating Appointments')}
                </label>
                {pending.repeatEnabled ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="space-y-1 text-xs font-semibold text-slate-600">
                      {tr('Frequency')}
                      <select className={fieldClass} value={pending.repeatFrequency} onChange={(event) => setPending((prev) => ({ ...prev, repeatFrequency: event.target.value as 'daily' | 'weekly' | 'monthly' }))}>
                        <option value="daily">{tr('Daily')}</option>
                        <option value="weekly">{tr('Weekly')}</option>
                        <option value="monthly">{tr('Monthly')}</option>
                      </select>
                    </label>
                    <label className="space-y-1 text-xs font-semibold text-slate-600">
                      {tr('Occurrences')}
                      <input className={fieldClass} type="number" min={1} max={24} value={pending.repeatCount} onChange={(event) => setPending((prev) => ({ ...prev, repeatCount: Number(event.target.value) || 1 }))} />
                    </label>
                  </div>
                ) : null}
              </div>

              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 md:col-span-2">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{tr('Calendar Payments')}</p>
                  <p className="text-xs text-slate-500">
                    {pending.classBookingEnabled
                      ? tr('Process class payments from the calendar for the full class booking or participant deposits.')
                      : tr('Capture deposits or place a payment hold during booking.')}
                  </p>
                  {pending.classBookingEnabled ? (
                    <p className="mt-1 text-xs font-semibold text-emerald-700">
                      {1 + pending.classParticipantIds.length} {tr('participants')} · {tr('Class Payments')}
                    </p>
                  ) : null}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={pending.bookingDepositEnabled}
                      onChange={(event) => setPending((prev) => ({ ...prev, bookingDepositEnabled: event.target.checked }))}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold">{tr('Booking Deposits')}</span>
                      <input
                        className={`${fieldClass} mt-2`}
                        type="number"
                        min={0}
                        step="0.01"
                        value={pending.bookingDepositAmount}
                        onChange={(event) => setPending((prev) => ({ ...prev, bookingDepositAmount: Number(event.target.value) || 0 }))}
                        disabled={!pending.bookingDepositEnabled}
                      />
                    </span>
                  </label>
                  <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={pending.paymentHoldEnabled}
                      onChange={(event) => setPending((prev) => ({ ...prev, paymentHoldEnabled: event.target.checked }))}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold">{tr('Payment Hold')}</span>
                      <input
                        className={`${fieldClass} mt-2`}
                        value={pending.paymentHoldReference}
                        placeholder={tr('Hold reference')}
                        onChange={(event) => setPending((prev) => ({ ...prev, paymentHoldReference: event.target.value }))}
                        disabled={!pending.paymentHoldEnabled}
                      />
                    </span>
                  </label>
                </div>
              </div>

              <div className="space-y-1 md:row-start-5">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <MapPin size={14} /> {tr('Title / Reason')}
                </label>
                <input
                  className={fieldClass}
                  placeholder={tr('Reason for visit or notes')}
                  value={pending.title}
                  onChange={(e) => setPending({ ...pending, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-xs font-semibold text-slate-600">{tr('Reminders')}</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-[auto_auto_minmax(150px,1fr)]">
                  <label className="flex items-center gap-2 px-1 py-1.5 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={pending.emailReminderEnabled}
                      onChange={(event) =>
                        setPending((prev) => ({ ...prev, emailReminderEnabled: event.target.checked }))
                      }
                    />
                    {tr('Email')}
                  </label>
                  <label className="flex items-center gap-2 px-1 py-1.5 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={pending.smsReminderEnabled}
                      onChange={(event) => setPending((prev) => ({ ...prev, smsReminderEnabled: event.target.checked }))}
                    />
                    {tr('Text')}
                  </label>
                  <select
                    className={fieldClass}
                    value={pending.reminderMinutesBefore}
                    onChange={(event) =>
                      setPending((prev) => ({ ...prev, reminderMinutesBefore: Number(event.target.value) }))
                    }
                    disabled={!pending.emailReminderEnabled && !pending.smsReminderEnabled}
                  >
                    <option value={60}>{tr('1 hour before')}</option>
                    <option value={180}>{tr('3 hours before')}</option>
                    <option value={1440}>{tr('1 day before')}</option>
                    <option value={2880}>{tr('2 days before')}</option>
                    <option value={10080}>{tr('1 week before')}</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">{tr('Schedulable assets')}</label>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  {filteredAssetOptions.length === 0 ? (
                    <p className="text-xs text-slate-500">{tr('No assets configured in Settings > Schedule Assets')}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {filteredAssetOptions.map((asset) => {
                        const selected = pending.assetResourceIds.includes(asset.id);
                        return (
                          <button
                            key={asset.id}
                            type="button"
                            title={requiredEquipmentIds.includes(asset.id) ? tr('Required by selected service') : undefined}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                              selected
                                ? 'border-ink bg-ink text-white'
                                : requiredEquipmentIds.includes(asset.id)
                                  ? 'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300'
                                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'
                            }`}
                            onClick={() => toggleAsset(asset.id)}
                          >
                            {asset.name}{requiredEquipmentIds.includes(asset.id) ? ` · ${tr('Required')}` : ''}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1 relative">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                  <FileText size={14} /> {tr('Forms')}
                </label>
                <input
                  className={fieldClass}
                  placeholder={tr('Search medical forms')}
                  value={medicalFormQuery}
                  onChange={(e) => {
                    setMedicalFormQuery(e.target.value);
                    setShowMedicalFormResults(true);
                  }}
                  onFocus={() => setShowMedicalFormResults(true)}
                />
                {showMedicalFormResults && filteredMedicalFormOptions.length > 0 ? (
                  <div className="absolute top-full left-0 right-0 mt-2 z-20 rounded-xl border border-slate-200 bg-white shadow-xl max-h-56 overflow-y-auto">
                    {filteredMedicalFormOptions.map((form) => (
                      <button
                        key={form.id}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-slate-50"
                        onMouseDown={() => addMedicalForm(form.id)}
                      >
                        <div className="text-sm font-medium text-slate-800">{form.name}</div>
                        <div className="text-xs text-slate-500">
                          {[form.code, form.category].filter(Boolean).join(' | ') || tr('Medical form')}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
                {selectedMedicalForms.length ? (
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      {selectedMedicalForms.map((form) => (
                        <span
                          key={form.templateId}
                          className={cn(
                            'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold',
                            form.isLocked
                              ? 'border-sky-200 bg-sky-50 text-sky-700'
                              : 'border-emerald-200 bg-emerald-50 text-emerald-700',
                          )}
                        >
                          <span>{form.templateName}</span>
                          {form.status ? (
                            <span className="text-[10px] uppercase tracking-wide">{form.status.replace(/_/g, ' ')}</span>
                          ) : null}
                          {!form.isLocked ? (
                            <button
                              type="button"
                              className="text-emerald-700/80 hover:text-emerald-900"
                              onClick={() => removeMedicalForm(form.templateId)}
                            >
                              ×
                            </button>
                          ) : null}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                <p className="text-xs text-slate-500">
                  {tr('Saving will send any newly added forms to the patient and create entries in Patient Medical Forms.')}
                </p>
              </div>
            </div>

            {isTelemedType && (
              
              <div>
                <div>
                <h4 className="font-semibold text-slate-800">{tr('Telemed Details')}</h4>
                <p className="text-xs text-slate-500">{tr('Link and details on your telemedicine session')}</p>
              
              </div>
              <div className="mt-4  bg-sky-50 rounded-2xl border border-sky-100 bg-sky-50/60 p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Video size={18} className="text-sky-600" />
                  <div>
                    <h4 className="font-semibold text-slate-800"></h4>
                    <p className="text-xs text-slate-500">
                      {tr('Before starting the telemedicine session, please ensure the patient understands that the consultation is being conducted remotely and may have limitations compared to an in-person exam.')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 md:gap-6">
                  <div className="flex-1 min-w-[200px] text-sm text-slate-700 break-all">
                    {pending.joinUrl ? (
                      <span className="block truncate text-xs text-slate-500" title={pending.joinUrl}>
                        {pending.joinUrl}
                      </span>
                    ) : (
                      <span className="text-slate-500">{tr('Join link will be created when you save this appointment.')}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap md:flex-nowrap md:self-center">
                    {pending.joinUrl && (
                      <button
                        type="button"
                        onClick={() => window.open(pending.joinUrl as string, '_blank', 'noopener,noreferrer')}
                        className="px-4 py-2 rounded-lg bg-ink text-white text-sm font-semibold shadow-sm hover:opacity-90 transition"
                      >
                        {tr('Join call')}
                      </button>
                    )}
                    {!pending.joinUrl && (
                      <label className="flex items-center gap-2 text-[12px] text-slate-700">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          checked={pending.generateTelemedLink !== false}
                          onChange={(e) =>
                            setPending((prev) => ({
                              ...prev,
                              generateTelemedLink: e.target.checked,
                            }))
                          }
                        />
                        {tr('Generate join link on save')}
                      </label>
                    )}
                  </div>
                </div>
              </div>
              </div>
            )}
          </section>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {pending.id ? (
              <div className="relative" ref={quickActionsRef}>
                <button
                  type="button"
                  onClick={() => setQuickActionsOpen((current) => !current)}
                  className={cn(
                    'inline-flex h-10 items-center justify-center gap-2 rounded-lg border px-4 text-sm font-semibold transition',
                    quickActionsOpen
                      ? 'border-slate-300 bg-white text-slate-900 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
                  )}
                  aria-haspopup="menu"
                  aria-expanded={quickActionsOpen}
                >
                  {tr('Quick actions')}
                  <ChevronDown size={14} className={cn('transition-transform', quickActionsOpen ? 'rotate-180' : '')} />
                </button>

                {quickActionsOpen ? (
                  <div className="absolute bottom-full left-0 z-20 mb-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl" role="menu">
                    <button
                      type="button"
                      onClick={() => {
                        setQuickActionsOpen(false);
                        void startOrOpenVisit();
                      }}
                      disabled={!canStartVisitFromModal || visitActionBusy}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      role="menuitem"
                    >
                      <span>{visitActionBusy ? tr('Opening visit...') : tr(pending.encounterId ? 'Open visit' : 'Start visit')}</span>
                      <Stethoscope size={14} className="text-slate-400" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setQuickActionsOpen(false);
                        void runAppointmentAutomation('check-in');
                      }}
                      disabled={automationBusyAction !== null || ['CHECKED_IN', 'COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(pending.status)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      role="menuitem"
                    >
                      <span>{automationBusyAction === 'check-in' ? tr('Checking in...') : tr('Check in')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setQuickActionsOpen(false);
                        void runAppointmentAutomation('no-show');
                      }}
                      disabled={automationBusyAction !== null || ['NO_SHOW', 'COMPLETED', 'CANCELLED'].includes(pending.status)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                      role="menuitem"
                    >
                      <span>{automationBusyAction === 'no-show' ? tr('Updating...') : tr('No-show')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setQuickActionsOpen(false);
                        void runAppointmentAutomation('schedule-reminders');
                      }}
                      disabled={automationBusyAction !== null}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      role="menuitem"
                    >
                      <span>{automationBusyAction === 'schedule-reminders' ? tr('Scheduling...') : tr('Reminders')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setQuickActionsOpen(false);
                        void runAppointmentAutomation('care-tasks');
                      }}
                      disabled={automationBusyAction !== null}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      role="menuitem"
                    >
                      <span>{automationBusyAction === 'care-tasks' ? tr('Creating...') : tr('Care tasks')}</span>
                    </button>
                    {pending.patientId ? (
                      <>
                        <div className="my-1 border-t border-slate-100" />
                        <button
                          type="button"
                          onClick={() => {
                            setQuickActionsOpen(false);
                            void openOrCreateAppointmentNote();
                          }}
                          disabled={noteActionBusy}
                          className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          role="menuitem"
                        >
                          <span>
                            {noteActionBusy
                              ? tr('Opening note...')
                              : appointmentNoteId
                              ? tr('View appointment note')
                              : tr('Generate appointment note')}
                          </span>
                          <FileText size={14} className="text-slate-400" />
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="inline-flex h-10 items-center justify-center px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-white"
            >
              {tr('Cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex h-10 items-center justify-center px-4 rounded-lg bg-ink text-sm font-semibold text-white shadow-soft disabled:opacity-60"
            >
              {saving ? tr('Saving...') : tr('Save')}
            </button>
          </div>
        </div>
      </div>
    </div>

    {showCreatePatientDialog ? (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45 p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{tr('Create patient')}</h3>
              <p className="text-xs text-slate-500">{tr('Add the patient, then continue scheduling this appointment.')}</p>
            </div>
            <button
              type="button"
              onClick={closeCreatePatientDialog}
              className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label={tr('Close')}
            >
              <Plus size={22} className="rotate-45" />
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            {createPatientError ? (
              <div className="rounded-lg border border-rose-100 bg-rose-50 p-3 text-sm text-rose-600">{createPatientError}</div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {tr('First name')}
                <input
                  autoFocus
                  className={fieldClass}
                  value={createPatientForm.firstName}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, firstName: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {tr('Last name')}
                <input
                  className={fieldClass}
                  value={createPatientForm.lastName}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, lastName: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {tr('DOB')}
                <input
                  type="date"
                  className={fieldClass}
                  value={createPatientForm.dob}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, dob: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {tr('MRN')}
                <input
                  className={fieldClass}
                  value={createPatientForm.mrn}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, mrn: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {tr('Phone')}
                <input
                  className={fieldClass}
                  value={createPatientForm.phone}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, phone: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                {tr('Email')}
                <input
                  type="email"
                  className={fieldClass}
                  value={createPatientForm.email}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, email: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600 md:col-span-2">
                {tr('Sex')}
                <select
                  className={fieldClass}
                  value={createPatientForm.sex}
                  onChange={(event) => setCreatePatientForm((prev) => ({ ...prev, sex: event.target.value }))}
                >
                  <option value="">{tr('Select sex')}</option>
                  <option value="FEMALE">{tr('Female')}</option>
                  <option value="MALE">{tr('Male')}</option>
                  <option value="OTHER">{tr('Other')}</option>
                  <option value="UNKNOWN">{tr('Unknown')}</option>
                </select>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={closeCreatePatientDialog}
              disabled={createPatientSaving}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              {tr('Cancel')}
            </button>
            <button
              type="button"
              onClick={createAndSelectPatient}
              disabled={createPatientSaving}
              className="inline-flex h-10 items-center justify-center rounded-lg bg-ink px-4 text-sm font-semibold text-white shadow-soft disabled:opacity-60"
            >
              {createPatientSaving ? tr('Saving...') : tr('Create patient')}
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}
